import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import {
  fetchEstablecerSesionOAuth,
  fetchMcpConnections,
  fetchRevocarMcpConnection,
  McpConnectionStatus,
} from "../services/mcpConnectionService";

type Asistente = {
  nombre: "Claude" | "ChatGPT";
  ayuda: string;
  pasosExternos: string[];
};

const asistentes: Asistente[] = [
  {
    nombre: "Claude",
    ayuda: "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp",
    pasosExternos: [
      "En Claude, abrí Configuración y luego Conectores.",
      "Elegí agregar un conector personalizado y pegá la dirección de GenFE.",
      "Cuando Claude lo solicite, elegí OAuth. Si pide un Client ID, copiá el identificador opcional que aparece abajo.",
    ],
  },
  {
    nombre: "ChatGPT",
    ayuda: "https://developers.openai.com/api/docs/guides/custom-mcp-server",
    pasosExternos: [
      "En ChatGPT web, abrí Plugins y elegí Agregar servidor MCP personalizado.",
      "Pegá la dirección de GenFE, elegí OAuth y creá la conexión.",
      "ChatGPT reconoce su identidad automáticamente; no tenés que copiar un Client ID.",
    ],
  },
];

export const obtenerEndpointMcp = () => {
  const url = new URL(
    window.location.origin,
    window.location.origin,
  );
  url.pathname = "/mcp";
  url.search = "";
  url.hash = "";
  return url.toString();
};

const ConexionesIA = () => {
  const { getUser } = useUserContext();
  const user = getUser();
  const [connections, setConnections] = useState<McpConnectionStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [wizard, setWizard] = useState<Asistente | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [checkMessage, setCheckMessage] = useState("");
  const mcpEndpoint = useMemo(obtenerEndpointMcp, []);

  const cargarConexiones = useCallback(async () => {
    if (!user) return null;
    setLoading(true);
    setError("");
    try {
      await fetchEstablecerSesionOAuth(user);
      const updatedConnections = await fetchMcpConnections(user);
      setConnections(updatedConnections);
      return updatedConnections;
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "No se pudieron consultar las conexiones.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const verificarConexion = useCallback(async (mostrarResultado: boolean) => {
    if (!user || !wizard) return;
    setChecking(true);
    setError("");
    try {
      const updatedConnections = await fetchMcpConnections(user);
      setConnections(updatedConnections);
      const connection = updatedConnections.find((item) => item.provider === wizard.nombre);
      if (connection?.connected) {
        setActiveStep(2);
        setCheckMessage(`${wizard.nombre} ya está conectado a ${user.conexion ?? "esta base"}.`);
        if (mostrarResultado) setMessage(`${wizard.nombre} quedó conectado a esta base.`);
      } else if (mostrarResultado) {
        setCheckMessage("Todavía no vemos la autorización. Terminá el paso en el asistente y volvé a comprobar.");
      }
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "No se pudo comprobar la conexión.");
    } finally {
      setChecking(false);
    }
  }, [user, wizard]);

  useEffect(() => {
    void cargarConexiones();
  }, [cargarConexiones]);

  useEffect(() => {
    if (!wizard) return undefined;

    const comprobarAlVolver = () => {
      if (document.visibilityState === "visible") void verificarConexion(false);
    };
    document.addEventListener("visibilitychange", comprobarAlVolver);
    return () => {
      document.removeEventListener("visibilitychange", comprobarAlVolver);
    };
  }, [wizard, verificarConexion]);

  const abrirAsistente = (asistente: Asistente) => {
    setWizard(asistente);
    setActiveStep(0);
    setCheckMessage("");
    setError("");
  };

  const copiar = async (valor: string, etiqueta: string) => {
    try {
      await navigator.clipboard.writeText(valor);
      setMessage(`${etiqueta} copiada.`);
    } catch {
      setError("No se pudo copiar automáticamente. Seleccioná y copiá el texto mostrado.");
    }
  };

  const revocar = async (provider: string) => {
    if (!user || !window.confirm(`¿Desconectar ${provider} de esta base?`)) return;
    try {
      await fetchRevocarMcpConnection(user, provider);
      const updatedConnections = await cargarConexiones();
      if (!updatedConnections) {
        setMessage("");
        setError(`Se solicitó desconectar ${provider}, pero no se pudo confirmar el estado actualizado.`);
        return;
      }

      const connection = updatedConnections.find((item) => item.provider === provider);
      if (connection?.connected) {
        setMessage("");
        setError(`El servidor todavía informa ${provider} como conectado. La autorización no quedó revocada.`);
        return;
      }

      setMessage(`${provider} ya no puede acceder a esta base desde GenFE. Si todavía aparece en ${provider}, quitá GenFE también desde la configuración de conectores del asistente.`);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "No se pudo desconectar el asistente.");
    }
  };

  const anyAvailable = connections.some((connection) => connection.available);
  const wizardConnection = wizard && connections.find((item) => item.provider === wizard.nombre);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: "auto" }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Conexiones con IA
          </Typography>
          <Typography color="text.secondary">
            Conectá Claude o ChatGPT para consultar los reportes permitidos y, si tu usuario tiene permiso de compras,
            buscar proveedores y revisar facturas o remitos antes de registrarlos. Crear proveedores también requiere
            permiso para administrar proveedores y autorización de escritura en esa conexión.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Si no aparecen las acciones de compra en el asistente después de actualizar GenFE, actualizá o volvé a
            conectar el servidor MCP desde ese asistente.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            Empresa/base activa: <strong>{user?.conexion ?? "sesión GenFE"}</strong>
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {message && <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert>}
        {!loading && !anyAvailable && (
          <Alert severity="warning">
            Las conexiones con asistentes todavía no están habilitadas para esta base. Pedile al administrador que
            active “Reportes para asistentes de IA”. Tus reportes web siguen funcionando.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {asistentes.map((asistente) => {
              const connection = connections.find((item) => item.provider === asistente.nombre);
              return (
                <Card key={asistente.nombre} variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <AutoAwesomeIcon color="primary" />
                      <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>{asistente.nombre}</Typography>
                      <Chip
                        label={!connection?.available ? "No habilitado" : connection.connected ? "Conectado" : "Sin conectar"}
                        color={connection?.connected ? "success" : "default"}
                        size="small"
                      />
                    </Stack>
                    <Typography color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                      {connection?.connected
                        ? `Puede consultar reportes de esta base${connection.authorizedAt ? ` desde ${new Date(connection.authorizedAt).toLocaleDateString("es-AR")}` : ""}.`
                        : "La configuración inicial toma unos minutos. Después, solo necesitás iniciar sesión y autorizar."}
                    </Typography>
                    {connection?.connected ? (
                      <Button variant="outlined" color="error" onClick={() => void revocar(asistente.nombre)}>
                        Desconectar
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        disabled={!connection?.available}
                        onClick={() => abrirAsistente(asistente)}
                      >
                        Configurar {asistente.nombre}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        <Alert severity="info" icon={<CheckCircleOutlineIcon />}>
          GenFE comparte con el asistente los datos de las consultas que realices. No recibe tu contraseña.
          Las acciones de compra sólo quedan disponibles si tus permisos las permiten y las autorizás al conectar.
          Claude y ChatGPT se conectan por separado y pueden usarse a la vez.
        </Alert>
      </Stack>

      <Dialog open={Boolean(wizard)} onClose={() => setWizard(null)} fullWidth maxWidth="sm">
        <DialogTitle>Conectar {wizard?.nombre} con GenFE</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>Antes de empezar</StepLabel>
              <StepContent>
                <Typography>
                  Vas a conectar <strong>{wizard?.nombre}</strong> con la base <strong>{user?.conexion ?? "activa"}</strong>.
                  El asistente verá solamente los reportes permitidos para tu usuario.
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Mantené abierta esta sesión de GenFE. La primera vez, la plataforma te pedirá iniciar sesión y aprobar el acceso.
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => setActiveStep(1)}>
                  Continuar
                </Button>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Agregar GenFE en {wizard?.nombre}</StepLabel>
              <StepContent>
                <Stack spacing={1.5}>
                  {wizard?.pasosExternos.map((paso, index) => (
                    <Typography key={paso}>
                      <strong>{index + 1}.</strong> {paso}
                    </Typography>
                  ))}
                  <Divider />
                  <Typography variant="subtitle2">Dirección de GenFE</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => void copiar(mcpEndpoint, "Dirección de GenFE")}
                  >
                    Copiar dirección
                  </Button>
                  {wizard?.nombre === "Claude" && (
                    <Box>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => void copiar(wizardConnection?.clientId ?? "genfe-claude", "Identificador de Claude")}
                      >
                        Copiar Client ID (solo si Claude lo pide)
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Es un identificador público; dejá vacía la contraseña o secreto si Claude muestra ese campo.
                      </Typography>
                    </Box>
                  )}
                  <Button
                    component="a"
                    href={wizard?.ayuda}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<LaunchIcon />}
                  >
                    Abrir guía de {wizard?.nombre}
                  </Button>
                  <Button variant="contained" onClick={() => setActiveStep(2)}>
                    Ya agregué GenFE
                  </Button>
                </Stack>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Autorizar y comprobar</StepLabel>
              <StepContent>
                <Typography>
                  Volvé a {wizard?.nombre}, elegí conectar GenFE e iniciá sesión si te lo pide. Revisá que la pantalla de
                  consentimiento muestre tu usuario y esta base antes de aprobar.
                </Typography>
                {checkMessage && <Alert sx={{ mt: 1.5 }} severity={wizardConnection?.connected ? "success" : "info"}>{checkMessage}</Alert>}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    disabled={checking}
                    onClick={() => void verificarConexion(true)}
                  >
                    {checking ? "Comprobando…" : "Ya autoricé, comprobar"}
                  </Button>
                  {wizardConnection?.connected && (
                    <Button onClick={() => setWizard(null)}>Listo</Button>
                  )}
                </Stack>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizard(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConexionesIA;
